import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import styles from './ForgotPasswordPage.module.css'
import AuthService from '../../features/auth/api/AppService'
import { routes } from '../../shared/config/routes'
import { FieldWrapper } from '../../shared/ui'
import { Button } from '../../shared/ui/controls/Button/Button'
import { FormInput } from '../../shared/ui/controls/FormInput'
import { getErrorMessage } from '../../shared/helpers/getErrorMessage'

const schema = z.object({
  email: z
    .string()
    .min(1, 'Email ist erforderlich')
    .regex(/^\S+@\S+\.\S+$/, 'Bitte eine gültige Email eingeben'),
})

type FormValues = z.infer<typeof schema>

const ForgotPasswordPage = () => {
  const [sent, setSent] = useState(false)

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  })

  const onSubmit = handleSubmit(async ({ email }) => {
    clearErrors('root')
    try {
      await AuthService.forgotPassword(email)
      setSent(true)
    } catch (err) {
      setError('root', { type: 'server', message: getErrorMessage(err) })
    }
  })

  return (
    <div className={styles.page}>
      <div className={styles.main}>
        {sent ? (
          <>
            <p className={styles.successText}>
              Falls ein Konto mit dieser Adresse existiert, haben wir Ihnen einen
              Link zum Zurücksetzen des Passworts gesendet.<br />
              Der Link ist 15 Minuten gültig.
            </p>
            <div className={styles.footer}>
              <Link to={routes.login}>Zurück zur Anmeldung</Link>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate className='!w-[300px] flex flex-col gap-[20px]'>
            <FieldWrapper label='Email'>
              <FormInput
                name="email"
                control={control}
                autoComplete="email"
                type="email"
                placeholder="Email eingeben"
                clearable
              />
            </FieldWrapper>

            {errors.root?.message && (
              <div className={styles.error} role="alert">
                {errors.root.message}
              </div>
            )}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Senden…' : 'Link senden'}
            </Button>

            <div className={styles.footer}>
              <Link to={routes.login}>Zurück zur Anmeldung</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default ForgotPasswordPage
